import { ledgerDivisions, ledgerStrings } from '@data/ledger';
import { partnerStrings } from '@data/partners';
import {
  EXTERNAL_AN_CREATOR_EMAIL,
  payeeStrings,
  paymentDetailsStrings,
  paymentSlipStrings,
} from '@data/paymentSlip';
import { preAlertStrings } from '@data/preAlert';
import { AdminInquiryDetailHelper, type AdminInquiryDetail } from '@models/BookingInquiry';
import { BookingInquiryFields } from '@models/next/BookingInquiry';
import type { CurrencyRate } from '@models/next/CurrencyRate';
import type { DeliveryRequestListItem } from '@models/next/DeliveryRequest';
import { FileTypeHelper, OptionalLocalFile, RemoteFile } from '@models/next/FileType';
import { LedgerAccount } from '@models/next/LedgerAccount';
import { Currency } from '@models/next/Money';
import { PaginationType } from '@models/next/PaginationType';
import { PreAlertFields } from '@models/next/PreAlert';
import { PreAlertDetails } from '@models/next/PreAlert';
import { AdminUser } from '@models/next/User';
import { TrackingData } from '@models/TrackingData';
import { BaseUser } from '@models/User';
import { empty } from '@monoid-dev/mobx-zod-form';
import { makeMessagesEitherLeftIf, makeMessagesEitherRightIf } from '@utils/errors';
import {
  firstLeftEither,
  makeMessagesEitherFromZod,
  makeMessagesEitherLeft,
  makeMessagesEitherRight,
  MessagesEither,
} from '@utils/errors';
import { f } from '@utils/nextForms';
import { counter, DATE_REGEX, mapOptional, roundPrice } from '@utils/types';
import dayjs from 'dayjs';
import { Either } from 'fp-ts/lib/Either';
import * as R from 'remeda';
import { z } from 'zod';

export const PaymentSlipPutAction = z.enum(['UNLOCK', 'SEND']);
export type PaymentSlipPutAction = z.infer<typeof PaymentSlipPutAction>;

export const PaymentDetailsExciseTaxType = z.enum(['非課税', '課税8%', '課税10%']);
export type PaymentDetailsExciseTaxType = z.infer<typeof PaymentDetailsExciseTaxType>;

export const PaymentSlipPaymentType = z.enum(['締払い', '現払い']);
export type PaymentSlipPaymentType = z.infer<typeof PaymentSlipPaymentType>;

export const PaymentSlipStatusName = z.enum(['下書き', '作成済', '修正中']);
export type PaymentSlipStatusName = z.infer<typeof PaymentSlipStatusName>;

export const PaymentSlipPaymentStatusName = z.enum(['未', '済']);
export type PaymentSlipPaymentStatusName = z.infer<typeof PaymentSlipPaymentStatusName>;

export const PaymentSlipStatus = z.object({
  name: PaymentSlipStatusName,
  time: z.string(),
});

export const PaymentSlipPaymentStatus = z.object({
  name: PaymentSlipPaymentStatusName,
  time: z.string(),
});

export const Payee = z
  .object({
    id: z.number().label(payeeStrings.payee),
    name_ja: z.string().label(payeeStrings.name_ja),
    name_en: z.string().label(payeeStrings.name_en),
    fc_no: z.string().label(payeeStrings.fc_no),
    principal_name: z.string().label(payeeStrings.principal_name),
    phone: z.string().label(payeeStrings.phone),
    email: z.string().label(payeeStrings.email),
    partner_id: z.number().nullable(),
    registration_number: z.string().nullable().label(partnerStrings.registrationNumber),
  })
  .label(payeeStrings.payee);

export type Payee = z.infer<typeof Payee>;

export const PaymentDetails = z.object({
  id: z.number(),
  cost_id: z.number().label(paymentDetailsStrings.cost_id),
  name: z.string().label(paymentDetailsStrings.name),
  memo: z.string().label(paymentDetailsStrings.memo),
  unit_price: z.number().label(paymentDetailsStrings.unit_price),
  unit_num: z.number().label(paymentDetailsStrings.unit_num),
  unit_num_type: z.string().label(paymentDetailsStrings.unit_num_type),
  excise_tax_type: PaymentDetailsExciseTaxType.label(paymentDetailsStrings.excise_tax_type),
  excise_tax: z.number().label(paymentDetailsStrings.excise_tax),
  debit: LedgerAccount.label(ledgerStrings.division),
  credit: LedgerAccount,
});

export type PaymentDetails = z.infer<typeof PaymentDetails>;

export const BasePaymentDetails = PaymentDetails.pick({
  cost_id: true,
  unit_price: true,
  unit_num: true,
  unit_num_type: true,
  excise_tax_type: true,
});

export type BasePaymentDetails = z.infer<typeof BasePaymentDetails>;

export const DefaultPaymentDetails = z.object({
  cost_id: PaymentDetails.shape.cost_id,
  unit_price: PaymentDetails.shape.unit_price.nullish(),
  unit_num: PaymentDetails.shape.unit_num.nullish(),
  unit_num_type: PaymentDetails.shape.unit_num_type,
  excise_tax_type: PaymentDetails.shape.excise_tax_type,
  debit: PaymentDetails.shape.debit,
  credit: PaymentDetails.shape.credit,
});

export type DefaultPaymentDetails = z.infer<typeof DefaultPaymentDetails>;

export const BatchInfo = z.object({
  id: z.number(),
  batch_no: z.string().label(paymentSlipStrings.batch_no),
});

export const PaymentSlip = z
  .object({
    id: z.number(),
    invoice_no: z.string().label(paymentSlipStrings.invoice_no),
    invoice_date: f.dateString().label(paymentSlipStrings.invoice_date),
    booking_inquiry: BookingInquiryFields.pick({ d2d_no: true, consignee: true }),
    pre_alert: PreAlertFields.pick({ job_code: true }),
    memo: z.string().label(paymentSlipStrings.memo),
    invoice_file: RemoteFile.nullish().label(paymentSlipStrings.invoice_file),
    payment_slip_no: z.string().label(paymentSlipStrings.payment_slip_no),
    fc_payment_slip_no: z.string().nullish().label(paymentSlipStrings.fc_payment_slip_no),
    payment_type: PaymentSlipPaymentType.label(paymentSlipStrings.payment_type),
    cutoff_date: f.dateString().nullish().label(paymentSlipStrings.deadline_shimebarai),
    payment_date: f.dateString().nullish().label(paymentSlipStrings.payment_date_shimebarai),
    currency: Currency.label(paymentSlipStrings.currency),
    /**
     * Zero if not selected
     */
    conversion_rate: z.number().label(paymentSlipStrings.conversion_rate),
    conversion_rate_date: f.dateString().nullish().label(paymentSlipStrings.conversion_rate_date),
    payment_details: PaymentDetails.array(),
    payee: Payee,
    status: PaymentSlipStatus.array().label(paymentSlipStrings.status),
    payment_status: PaymentSlipPaymentStatus.array().label(paymentSlipStrings.payment_status),
    arrival_notice_tag: z.boolean().label(paymentSlipStrings.arrival_notice_tag),
    created_at: z.string(),
    updated_at: z.string(),
    batch: BatchInfo.nullish(),
    creator: AdminUser,
    _total_amount: z.never().optional().label(paymentSlipStrings.total_amount),
  })
  .label(paymentSlipStrings.payment_slip);

export type PaymentSlip = z.infer<typeof PaymentSlip>;

export const PaymentSlipPages = z.object({
  count: z.number(),
  results: PaymentSlip.array(),
});

export type PaymentSlipPages = z.infer<typeof PaymentSlipPages>;

export const PostPaymentSlip = z.object({
  d2d_no: z.string(),
  payee: Payee,
  cutoff_date: PaymentSlip.shape.cutoff_date,
  payment_date: PaymentSlip.shape.payment_date,
  payment_type: PaymentSlip.shape.payment_type,
  invoice_file: PaymentSlip.shape.invoice_file,
  invoice_date: PaymentSlip.shape.invoice_date,
  invoice_no: PaymentSlip.shape.invoice_no,
  memo: PaymentSlip.shape.memo,
  arrival_notice_tag: PaymentSlip.shape.arrival_notice_tag,
  currency: PaymentSlip.shape.currency,
  conversion_rate: PaymentSlip.shape.conversion_rate,
  conversion_rate_date: PaymentSlip.shape.conversion_rate_date,
  delivery_request_id: z.number().optional(),
  payment_details: PaymentDetails.array(),
});

export type PostPaymentSlip = z.infer<typeof PostPaymentSlip>;

export const PatchPaymentSlip = PostPaymentSlip.partial();

export type PatchPaymentSlip = z.infer<typeof PatchPaymentSlip>;

export const PaymentSlipInfoForm = PaymentSlip.pick({
  payment_slip_no: true,
  payment_type: true,
  cutoff_date: true,
  payment_date: true,
  invoice_date: true,
  memo: true,
  currency: true,
  arrival_notice_tag: true,
})
  .extend({
    payee: Payee.label(payeeStrings.payee),
    invoice_no: PaymentSlip.shape.invoice_no,
    invoice_file: OptionalLocalFile.label(paymentSlipStrings.invoice_file),
    conversion_rate_date: PaymentSlip.shape.conversion_rate_date.label(
      paymentSlipStrings.conversion_rate,
    ),
  })
  .superRefine((data, ctx) => {
    if (data.payee.id === 0) {
      const e = z
        .object({
          payee: Payee.extend({
            name_ja: Payee.shape.name_ja.min(1),
          }),
          payment_type: PaymentSlip.shape.payment_type.refine(
            (r) => r === '現払い',
            paymentSlipStrings.errorPaymentTypeForOtherPayee,
          ),
        })
        .safeParse(data);
      !e.success && e.error.issues.forEach(ctx.addIssue);
    }
    if (data.payment_type === '締払い') {
      const e = z
        .object({
          payment_date: PaymentSlip.shape.payment_date.unwrap().unwrap(),
        })
        .safeParse(data);
      !e.success && e.error.issues.forEach(ctx.addIssue);
    }
  });

export interface PaymentSlipInfoForm extends z.infer<typeof PaymentSlipInfoForm> {}

export const PaymentSlipDetailsForm = PaymentDetails.pick({
  id: true,
  memo: true,
  unit_price: true,
  unit_num: true,
  excise_tax_type: true,
  debit: true,
})
  .extend({
    unit_price: f.yen().label(paymentDetailsStrings.unit_price),
    cost_id: PaymentDetails.shape.cost_id.label(paymentDetailsStrings.name),
    unit_num_type: PaymentDetails.shape.unit_num_type.min(1),
  })
  .array()
  .min(1);

export type PaymentSlipDetailsForm = z.infer<typeof PaymentSlipDetailsForm>;

export const PaymentSlipDetailsMeta = z
  .object({
    cost_id: z.number(),
    name: z.string(),
    unit_num_types: z.string().array(),
    unit_num: z.number().nullish(),
    excise_tax_type: PaymentDetailsExciseTaxType,
    debit: LedgerAccount,
    credit: LedgerAccount,
  })
  .array();

export type PaymentSlipDetailsMeta = z.infer<typeof PaymentSlipDetailsMeta>;

export const PaymentSlipSearchQuery = z
  .object({
    page: PaginationType.shape.page,
    page_size: PaginationType.shape.page_size,
    d2d_no: BookingInquiryFields.shape.d2d_no,
    payment_slip_no: PaymentSlip.shape.payment_slip_no,
    payee: z.string().label(payeeStrings.payee),
    job_code: z.string().label(preAlertStrings.job_code),
    payment_type: PaymentSlip.shape.payment_type,
    payment_date_after: PaymentSlip.shape.payment_date,
    payment_date_before: PaymentSlip.shape.payment_date,
    cutoff_date_after: PaymentSlip.shape.cutoff_date,
    cutoff_date_before: PaymentSlip.shape.cutoff_date,
    payment_status: PaymentSlipPaymentStatusName.label(paymentSlipStrings.payment_status),
    has_batch_no: z.boolean(),
    batch_no: BatchInfo.shape.batch_no,
    status: PaymentSlipStatusName,
    ordering: z.string().array(),
  })
  .partial();

export type PaymentSlipSearchQuery = z.infer<typeof PaymentSlipSearchQuery>;

export const PaymentSlipPaymentDatesQuery = z.object({
  payee_id: z.number(),
  payment_type: PaymentSlipPaymentType,
  invoice_date: z.string().trim().regex(DATE_REGEX, paymentSlipStrings.errorInvalidInvoiceDate),
});

export type PaymentSlipPaymentDatesQuery = z.infer<typeof PaymentSlipPaymentDatesQuery>;

export const PaymentSlipPaymentDates = z.object({
  cutoff_date: PaymentSlip.shape.cutoff_date,
  payment_date: PaymentSlip.shape.payment_date,
});

export type PaymentSlipPaymentDates = z.infer<typeof PaymentSlipPaymentDates>;

export class BasePaymentDetailsHelper {
  constructor(public data: BasePaymentDetails) {}

  static nextId = counter(Date.now());

  getTaxAmountPercentage() {
    return this.data.excise_tax_type === '非課税'
      ? 0
      : this.data.excise_tax_type === '課税10%'
      ? 0.1
      : this.data.excise_tax_type === '課税8%'
      ? 0.08
      : 0;
  }

  getTaxExcludedTotalPrice() {
    return roundPrice(roundPrice(this.data.unit_price, 'JPY') * this.data.unit_num, 'JPY');
  }

  getExciseTax() {
    /**
     * 公官庁への請求する消費税の端数処理は四捨五入
     * @see: https://pca.jp/p-tips/articles/fl200703.html
     */
    return roundPrice(this.getTaxAmountPercentage() * this.getTaxExcludedTotalPrice(), 'JPY');
  }

  getTatekaeTotalPrice() {
    return roundPrice(this.getTaxExcludedTotalPrice() + this.getExciseTax(), 'JPY');
  }

  static mapDefaultsToForm(d: DefaultPaymentDetails[]): PaymentSlipDetailsForm {
    return d.length === 0
      ? [
          {
            id: BasePaymentDetailsHelper.nextId(),
            cost_id: empty,
            debit: empty,
            excise_tax_type: empty,
            memo: empty,
            unit_price: empty,
            unit_num: empty,
            unit_num_type: empty,
          },
        ]
      : d.map((e) => ({
          ...e,
          id: BasePaymentDetailsHelper.nextId(),
          memo: empty,
          unit_price: e.unit_price ?? empty,
          unit_num: e.unit_num ?? empty,
          excise_tax_type: e.excise_tax_type,
        }));
  }
}

export class PaymentDetailsHelper extends BasePaymentDetailsHelper {
  constructor(public data: PaymentDetails) {
    super(data);
  }

  getTatekaeTotalPrice() {
    return roundPrice(this.getTaxExcludedTotalPrice() + this.data.excise_tax, 'JPY');
  }
}

export class PaymentSlipHelper {
  constructor(public data: PaymentSlip) {}

  static canFinishPaymentSlips(
    user: BaseUser,
    bookingInquiry: AdminInquiryDetail,
    trackingData: TrackingData,
    paymentSlips: PaymentSlip[],
  ): MessagesEither {
    return firstLeftEither(
      () => new AdminInquiryDetailHelper(bookingInquiry).canEditAsSalespersonJapan(user),
      () =>
        makeMessagesEitherRightIf(
          paymentSlips.every(
            (paymentSlip) => new PaymentSlipHelper(paymentSlip).getLastStatus()?.name === '作成済',
          ),
          {
            messages: [paymentSlipStrings.errorPaymentSlipNotCreated],
          },
        ),
      () =>
        trackingData.data.お届け完了
          ? makeMessagesEitherRight()
          : makeMessagesEitherLeft({
              messages: [paymentSlipStrings.errorBookingInquiryNotDelivered],
            }),
      () =>
        trackingData.data.支払伝票全部発行完了
          ? makeMessagesEitherLeft({
              messages: [paymentSlipStrings.errorAlreadyPaymentSlipDone],
            })
          : makeMessagesEitherRight(),
    );
  }

  getTotalAmount() {
    return roundPrice(
      R.sumBy(this.data.payment_details, (item) =>
        new PaymentDetailsHelper(item).getTatekaeTotalPrice(),
      ),
      'JPY',
    );
  }

  getTaxExcludedTotalPrice() {
    return R.pipe(
      this.data.payment_details,
      R.filter((item) => item.debit === '支払在庫'),
      R.sumBy((item) => new PaymentDetailsHelper(item).getTaxExcludedTotalPrice()),
      (p) => roundPrice(p, 'JPY'),
    );
  }

  getExciseTax() {
    return R.pipe(
      this.data.payment_details,
      R.sumBy((item) => new PaymentDetailsHelper(item).getExciseTax()),
      (p) => roundPrice(p, 'JPY'),
    );
  }

  getTatekaeTotalPrice() {
    return R.pipe(
      this.data.payment_details,
      R.filter((item) => item.debit === '立替金'),
      R.sumBy((item) => new PaymentDetailsHelper(item).getTaxExcludedTotalPrice()),
      (p) => roundPrice(p, 'JPY'),
    );
  }

  getLastStatus() {
    return R.last(this.data.status);
  }

  getLastPaymentStatus() {
    return R.last(this.data.payment_status);
  }

  static getPaymentSlipDetailsFormRowInitialValue(): PaymentSlipDetailsForm[number] {
    return {
      id: BasePaymentDetailsHelper.nextId(),
      cost_id: empty,
      debit: empty,
      excise_tax_type: empty,
      memo: empty,
      unit_num: 1,
      unit_num_type: empty,
      unit_price: empty,
    };
  }

  static canCreatePaymentSlip(user: BaseUser, preAlertDetails: PreAlertDetails): MessagesEither {
    return firstLeftEither(
      () =>
        makeMessagesEitherLeftIf(preAlertDetails.is_ledger_locked, {
          messages: [paymentSlipStrings.errorPreAlertLedgerLocked],
        }),
      () =>
        makeMessagesEitherRightIf(user.role === 'FCJapan' || user.role === 'admin', {
          messages: [paymentSlipStrings.errorNotFCJapan],
        }),
    );
  }

  static canUpdatePaymentSlip(
    user: BaseUser,
    preAlertDetails: PreAlertDetails,
    data: PaymentSlip,
    BookingInquiryDetails?: AdminInquiryDetail,
  ): MessagesEither {
    return firstLeftEither(
      () => this.canCreatePaymentSlip(user, preAlertDetails),
      () =>
        makeMessagesEitherRightIf(
          (user.role === 'FCJapan' && data.creator.email === user.email) ||
            user.role === 'admin' ||
            (data.creator.email === EXTERNAL_AN_CREATOR_EMAIL &&
              BookingInquiryDetails?.salesperson_japan?.email === user.email),
          {
            messages: [paymentSlipStrings.errorCannotEditPaymentSlip],
          },
        ),
    );
  }

  static canUnlockPaymentSlip(
    user: BaseUser,
    preAlertDetails: PreAlertDetails,
    paymentSlipDetails: PaymentSlip,
  ): MessagesEither {
    return firstLeftEither(
      () => AdminInquiryDetailHelper.canUnlock(user, preAlertDetails),
      () =>
        makeMessagesEitherLeftIf(R.last(paymentSlipDetails.payment_status)?.name === '済', {
          messages: [paymentSlipStrings.errorPaid],
        }),
    );
  }

  static maybeMapFormToPost(
    infoForm: PaymentSlipInfoForm,
    detailsForm: PaymentSlipDetailsForm,
    context: {
      bookingInquiry: AdminInquiryDetail;
      payees: Payee[];
      currencyRates: CurrencyRate[];
      paymentSlipDetailsMeta: PaymentSlipDetailsMeta;
      invoice_file?: RemoteFile | null;
      deliveryRequest?: DeliveryRequestListItem | null;
    },
  ): Either<{ messages: string[] }, PostPaymentSlip> {
    const messages: string[] = [];

    const conversion_rate = infoForm.conversion_rate_date
      ? context.currencyRates.find((c) => c.date === infoForm.conversion_rate_date)?.rate
      : 0;

    if (conversion_rate == null) {
      messages.push(paymentSlipStrings.errorConversionRateNotExist);
    }

    const payment_details: PaymentDetails[] = [];

    for (const details of detailsForm) {
      const meta = context.paymentSlipDetailsMeta.find((meta) => meta.cost_id === details.cost_id);
      if (!meta) {
        messages.push(paymentSlipStrings.errorCostIdNotFound(details.cost_id));
        continue;
      }

      const credit = Object.values(ledgerDivisions).find(
        (division) => division.debit === details.debit,
      )?.credit;
      if (!credit) {
        messages.push(ledgerStrings.errorDebitNotFound(details.debit));
        continue;
      }

      const paymentDetails: PaymentDetails = {
        ...details,
        name: meta.name,
        credit,
        excise_tax: new BasePaymentDetailsHelper(details).getExciseTax(),
      };

      if (!meta.unit_num_types.includes(details.unit_num_type)) {
        messages.push(
          paymentSlipStrings.errorInvalidUnitNumType(
            paymentDetails.name,
            paymentDetails.unit_num_type,
          ),
        );
        continue;
      }

      payment_details.push(paymentDetails);
    }

    if (messages.length) {
      return {
        _tag: 'Left',
        left: { messages },
      };
    }

    return {
      _tag: 'Right',
      right: {
        ...infoForm,
        invoice_file: context.invoice_file,
        d2d_no: context.bookingInquiry.d2d_no,
        conversion_rate: conversion_rate!,
        payment_details,
        delivery_request_id: context.deliveryRequest?.id,
      },
    };
  }

  static mapToInfoForm(paymentSlip: PaymentSlip): PaymentSlipInfoForm {
    return {
      ...paymentSlip,
      invoice_file: mapOptional(paymentSlip.invoice_file, (f) => FileTypeHelper.remoteToLocal(f)),
    };
  }

  static mapToDetailsForm(paymentSlip: PaymentSlip): PaymentSlipDetailsForm {
    return paymentSlip.payment_details;
  }

  static resolvePaymentSlipInfoFormInitialOutput(
    preAlert: PreAlertDetails,
    context: {
      isArrivalNoticeTag?: boolean;
      deliveryRequest?: DeliveryRequestListItem;
      currencyRates: CurrencyRate[];
      payees: Payee[];
    },
  ): PaymentSlipInfoForm {
    return {
      ...PaymentSlipInfoForm.getFormMeta().getInitialOutput(),
      payee:
        mapOptional(context.deliveryRequest?.partner, (partner) =>
          context.payees.find((payee) => payee.fc_no === partner.fc_no),
        ) ?? empty,
      arrival_notice_tag: context.deliveryRequest
        ? false
        : context.isArrivalNoticeTag
        ? true
        : false,
      invoice_date: (context.deliveryRequest
        ? dayjs(context.deliveryRequest.delivery_date)
        : dayjs()
      ).format('YYYY-MM-DD'),
      conversion_rate_date: context.currencyRates.some((r) => r.date === preAlert.etd)
        ? preAlert.etd
        : empty,
    };
  }

  static canComputePaymentDates(body: PaymentSlipPaymentDatesQuery) {
    return makeMessagesEitherFromZod(PaymentSlipPaymentDatesQuery.safeParse(body));
  }
}

export const examplePaymentSlip: PaymentSlip = {
  id: 1,
  invoice_no: 'AAAAABBCCC999',
  invoice_date: '2025-10-24',
  booking_inquiry: {
    d2d_no: 'Y-11451411',
  },
  pre_alert: {
    job_code: 'FT22CI0674',
  },
  memo: 'nothing',
  currency: 'JPY',
  invoice_file: null,
  payment_slip_no: 'Y-11451145P01',
  fc_payment_slip_no: 'FC114514',
  payment_type: '現払い',
  cutoff_date: '2023-11-11',
  payment_date: '2023-03-11',
  conversion_rate: 131.114,
  conversion_rate_date: '2023-03-11',
  payment_details: [
    {
      id: 1,
      cost_id: 1,
      name: '輸入通関料',
      memo: '',
      unit_price: 28000,
      unit_num: 1,
      unit_num_type: 'B/L',
      excise_tax_type: '非課税',
      excise_tax: 0,
      debit: '立替金',
      credit: '未払立替金',
    },
    {
      id: 2,
      cost_id: 2,
      name: '輸入事務取扱料',
      memo: '',
      unit_price: 14000,
      unit_num: 1,
      unit_num_type: 'B/L',
      excise_tax_type: '課税10%',
      excise_tax: 1400,
      debit: '支払在庫',
      credit: '買掛金',
    },
    {
      id: 3,
      cost_id: 15,
      name: '税関検査ﾄﾗｯｸ横持ち料',
      memo: '',
      unit_price: 14000,
      unit_num: 1,
      unit_num_type: '10T車',
      excise_tax_type: '課税10%',
      excise_tax: 1400,
      debit: '支払在庫',
      credit: '買掛金',
    },
  ],
  payee: {
    id: 1,
    name_ja: '西農運輸',
    name_en: 'SEINOU LOGIX',
    fc_no: 'FC114',
    principal_name: '西郷隆盛',
    phone: '09011451414',
    email: 'seino@monoid.co.jp',
    partner_id: 1,
    registration_number: '123123123',
  },
  status: [{ name: '作成済', time: '2023-03-22T04:40:02.787Z' }],
  payment_status: [{ name: '未', time: '2023-03-22T04:40:02.787Z' }],
  arrival_notice_tag: true,
  creator: {
    id: 2,
    last_name: 'Wang',
    first_name: 'Chenyu',
    cellphone: '123123123123',
    landline: '114514123123',
    sns_id: '123123123sd',
    email: 'wangchenyu2017@gmail.com',
    last_login: '2023-04-12T18:28:02.755044+09:00',
    role: 'admin',
    email_verified: true,
  },
  created_at: '2023-03-22T04:40:02.787Z',
  updated_at: '2023-03-22T04:40:02.787Z',
};

export const examplePaymentSlips: PaymentSlip[] = [
  examplePaymentSlip,
  { ...examplePaymentSlip, id: 2, payment_status: [], status: [] },
  {
    ...examplePaymentSlip,
    id: 3,
    status: [
      {
        name: '修正中',
        time: '2023-03-22T04:40:02.787Z',
      },
    ],
  },
  {
    ...examplePaymentSlip,
    id: 4,
    payment_status: [{ name: '済', time: '2023-03-22T04:40:02.787Z' }],
  },
];

export const examplePayee: Payee = {
  id: 1,
  name_ja: 'エフシースタンダードロジックス㈱東京支店',
  name_en: 'FCST',
  fc_no: 'FCS28702',
  principal_name: 'Ryou',
  phone: '',
  email: '',
  partner_id: 0,
  registration_number: '',
};

export const examplePayees: Payee[] = [
  {
    ...examplePayee,
  },
  {
    id: 2,
    name_ja: 'エフシースタンダードロジックス株式会社',
    name_en: 'FCS',
    fc_no: 'FCS15990',
    principal_name: 'Ryou',
    phone: '',
    email: '',
    partner_id: 0,
    registration_number: '',
  },
  {
    id: 3,
    name_ja: '関空運輸株式会社',
    name_en: 'KANKU',
    fc_no: 'FCS36562',
    principal_name: '豊留',
    phone: '',
    email: 'r-toyotome@kanku-unyu.com',
    partner_id: 1,
    registration_number: '123123123',
  },
  {
    id: 4,
    name_ja: '株式会社月島物流サービス',
    name_en: 'TSUKISHIMA',
    fc_no: 'FCS36105',
    principal_name: '佐藤',
    phone: '',
    email: 'masaru_sato@tsukishima-brs.co.jp',
    partner_id: 1,
    registration_number: '123123123',
  },
];

export const examplePaymentSlipDetailsMeta: PaymentSlipDetailsMeta = [
  {
    cost_id: 1,
    name: '輸入通関料',
    debit: '支払在庫',
    credit: '買掛金',
    unit_num_types: ['B/L'],
    unit_num: 1,
    excise_tax_type: '非課税',
  },
  {
    cost_id: 3,
    name: '輸入事務取扱料',
    debit: '支払在庫',
    credit: '買掛金',
    unit_num_types: ['件'],
    unit_num: 1,
    excise_tax_type: '課税10%',
  },
  {
    cost_id: 5,
    name: 'トラック賃',
    debit: '支払在庫',
    credit: '買掛金',
    unit_num_types: ['車', '4T車', '10T車', '増T車'],
    unit_num: 1,
    excise_tax_type: '課税10%',
  },
  {
    cost_id: 39,
    name: '横持トラック賃',
    debit: '支払在庫',
    credit: '買掛金',
    unit_num_types: ['車', '4T車', '10T車', '増T車'],
    unit_num: null,
    excise_tax_type: '課税10%',
  },
  {
    cost_id: 13,
    name: '税関検査料',
    debit: '支払在庫',
    credit: '買掛金',
    unit_num_types: ['件'],
    unit_num: 1,
    excise_tax_type: '非課税',
  },
  {
    cost_id: 15,
    name: '税関検査ﾄﾗｯｸ横持ち料',
    debit: '支払在庫',
    credit: '買掛金',
    unit_num_types: ['車', '4T車', '10T車', '増T車'],
    unit_num: 1,
    excise_tax_type: '非課税',
  },
  {
    cost_id: 52,
    name: '保険料',
    debit: '支払在庫',
    credit: '買掛金',
    unit_num_types: ['件'],
    unit_num: null,
    excise_tax_type: '非課税',
  },
  {
    cost_id: 72,
    name: '関税',
    debit: '立替金',
    credit: '未払立替金',
    unit_num_types: ['B/L'],
    unit_num: null,
    excise_tax_type: '非課税',
  },
  {
    cost_id: 73,
    name: '輸入消費税',
    debit: '立替金',
    credit: '未払立替金',
    unit_num_types: ['B/L'],
    unit_num: null,
    excise_tax_type: '非課税',
  },
  {
    cost_id: 74,
    name: '輸入地方消費税',
    debit: '立替金',
    credit: '未払立替金',
    unit_num_types: ['B/L'],
    unit_num: null,
    excise_tax_type: '非課税',
  },
  {
    cost_id: 43,
    name: '海上運賃',
    debit: '支払在庫',
    credit: '買掛金',
    unit_num_types: ['件'],
    unit_num: null,
    excise_tax_type: '非課税',
  },
  {
    cost_id: 44,
    name: 'CYチャ-ジ',
    debit: '支払在庫',
    credit: '買掛金',
    unit_num_types: ['件'],
    unit_num: null,
    excise_tax_type: '非課税',
  },
  {
    cost_id: 45,
    name: 'CFSチャージ',
    debit: '支払在庫',
    credit: '買掛金',
    unit_num_types: ['件'],
    unit_num: null,
    excise_tax_type: '非課税',
  },
  {
    cost_id: 36,
    name: '保管料',
    debit: '支払在庫',
    credit: '買掛金',
    unit_num_types: ['件', 'm3', '期'],
    unit_num: 1,
    excise_tax_type: '非課税',
  },
  {
    cost_id: 6,
    name: 'ドレー賃',
    debit: '支払在庫',
    credit: '買掛金',
    unit_num_types: ["20'", "40'"],
    unit_num: 1,
    excise_tax_type: '課税10%',
  },
  {
    cost_id: 65,
    name: 'その他料金',
    debit: '支払在庫',
    credit: '買掛金',
    unit_num_types: ['件'],
    unit_num: null,
    excise_tax_type: '課税10%',
  },
];

export const examplePaymentSlipInfoForm: PaymentSlipInfoForm = {
  payee: {
    id: 4,
    name_ja: '株式会社月島物流サービス',
    name_en: 'TSUKISHIMA',
    fc_no: 'FCS36105',
    principal_name: '佐藤',
    phone: '',
    email: 'masaru_sato@tsukishima-brs.co.jp',
    partner_id: 1,
    registration_number: '123123123',
  },
  payment_slip_no: 'Y11455141P01',
  payment_type: '現払い',
  cutoff_date: '2022-01-02',
  payment_date: '2022-01-02',
  invoice_no: 'AAABBBCCC999',
  invoice_date: '2022-01-02',
  invoice_file: undefined,
  memo: '',
  currency: 'JPY',
  conversion_rate_date: null,
  arrival_notice_tag: true,
};

export const examplePaymentSlipDetailsForm: PaymentSlipDetailsForm = [
  {
    id: 1,
    memo: 'MEMOMEMO',
    unit_price: 11451,
    unit_num: 3,
    excise_tax_type: '課税10%',
    cost_id: 1,
    unit_num_type: 'B/L',
    debit: '支払在庫',
  },
];
